"use client";

import Link from "next/link";
import {
  Container,
  Title,
  Text,
  Button,
  Stack,
  Group,
  Paper,
  Box,
  Divider,
  Accordion,
  Anchor,
  ThemeIcon,
  SimpleGrid,
  Badge,
  Image,
  Card,
  List,
} from "@mantine/core";
import { IconArrowRight, IconCamera, IconCheck, IconX } from "@tabler/icons-react";

type Faq = { question: string; answer: string };

const GAMINE_TYPES = [
  {
    id: "flamboyant-gamine",
    name: "Flamboyant Gamine",
    lead: "The mix leads sharp",
    body: "Yang wins the argument: crisp bones, angular edges, bold features on a compact frame. FGs own graphic haircuts and geometric styling no one else can wear.",
    icons: "Audrey Hepburn · Twiggy · Prince",
  },
  {
    id: "gamine",
    name: "Gamine",
    lead: "The even mix",
    body: "Neither sharp nor soft wins — features genuinely alternate, which is why the pure Gamine reads so animated. The rarest of the three in practice.",
    icons: "Leslie Caron · Jean Seberg · Mia Farrow",
  },
  {
    id: "soft-gamine",
    name: "Soft Gamine",
    lead: "The mix leads rounded",
    body: "Yin wins: big rounded eyes, full cheeks, a small pointed chin. The cute, doll-like end of the gamine family — sparkle rather than smolder.",
    icons: "Winona Ryder · Reese Witherspoon · Sarah Jessica Parker",
  },
];

const STYLE_RULES = [
  "Cropped jackets and cardigans that end at the hip — nothing longline",
  "Trousers and sleeves that stop at deliberate points: ankle, wrist, never dragging",
  "Contrast and pattern mixing — Breton stripes, polka dots, color blocking",
  "Crisp small-scale details: collars, bows, buttons, geometric jewelry",
  "Flats with personality — ballet flats, mary-janes, loafers",
  "Fitted through the body; definition over drape, always",
];

const STYLE_AVOID = [
  "Maxi dresses and floor-length coats (they bury a compact frame)",
  "Long unbroken columns of one color",
  "Oversized slouch — what flatters a Natural drowns a Gamine",
  "Heavy draping and fluid fabrics with no structure",
];

export default function WhatIsGamineContent({ faqs }: { faqs: Faq[] }) {
  return (
    <Container size="lg" py={48}>
      <Stack gap={48}>
        {/* Hero */}
        <Box>
          <Badge size="lg" variant="light" color="lime" mb={16}>
            The Complete Guide
          </Badge>
          <Title order={1} fz={{ base: 32, md: 44 }} fw={300} mb={16} style={{ letterSpacing: -1 }}>
            What Is a Gamine?
          </Title>
          <Text fz="xl" c="dimmed" maw={720} style={{ lineHeight: 1.7 }}>
            <em>Gamine</em> (ga-MEEN) is French, and it wasn&apos;t originally a compliment — it meant a
            street urchin, a scrappy kid loose on the streets of Paris. English borrowed the word and turned
            it into one of fashion&apos;s most durable archetypes: a small, slim woman with boyish charm,
            enormous expressive eyes, and an air of mischief. Audrey Hepburn made it a category. Jean Seberg
            made it cool. Sixty years later, stylists still reach for the word because nothing else quite
            describes it.
          </Text>
        </Box>

        {/* Hero image */}
        <Image
          src="/kibbe/gamine-style-icons.png"
          alt="Gamine style illustration: Breton stripes, mod shift dress, and cropped tailoring"
          radius="md"
        />

        {/* The word vs the body type */}
        <Paper p={{ base: "lg", md: "xl" }} radius="lg" withBorder>
          <Title order={2} fz={24} fw={400} mb={16}>
            Gamine the Word vs. Gamine the Body Type
          </Title>
          <Text fz="md" c="dimmed" style={{ lineHeight: 1.8 }} mb={16}>
            The word describes an aesthetic — cropped hair, bright eyes, playful androgyny. The body type is
            more specific. When David Kibbe built his 13 image identities, he used Gamine for the petite
            frames that mix opposing qualities: sharp jaw with round eyes, delicate bones with crisp edges,
            softness and angularity refusing to resolve. That refusal to resolve is the whole point. A
            Classic face agrees with itself; a Gamine face argues with itself, charmingly, on a small scale.
          </Text>
          <Text fz="md" c="dimmed" style={{ lineHeight: 1.8 }}>
            Scale is the non-negotiable. Gamines are typically 5&apos;5&quot; and under with small bones and
            short-to-moderate vertical lines. It&apos;s a concentrated type — a lot of feature in a small
            frame — and that concentration is what reads as &quot;spark.&quot; The animation isn&apos;t a
            personality trait; it&apos;s geometry.
          </Text>
        </Paper>

        {/* The three gamine types */}
        <Box>
          <Title order={2} fz={24} fw={400} mb={8}>
            The Three Gamine Body Types
          </Title>
          <Text fz="sm" c="dimmed" mb={24}>
            Kibbe splits the gamine family by which force leads the mix.
          </Text>
          <SimpleGrid cols={{ base: 1, md: 3 }} spacing={16}>
            {GAMINE_TYPES.map((t) => (
              <Card key={t.id} padding="lg" radius="md" withBorder>
                <Badge variant="light" color="lime" size="sm" mb={8}>
                  {t.lead}
                </Badge>
                <Text fw={600} mb={8}>{t.name}</Text>
                <Text fz="sm" c="dimmed" style={{ lineHeight: 1.6 }} mb={8}>
                  {t.body}
                </Text>
                <Text fz="xs" c="dimmed" mb={12}>
                  {t.icons}
                </Text>
                <Anchor component={Link} href={`/kibbe/${t.id}`} fz="sm">
                  Full {t.name} guide →
                </Anchor>
              </Card>
            ))}
          </SimpleGrid>
          <Group gap={12} mt={16}>
            <Button component={Link} href="/kibbe/compare/flamboyant-gamine/soft-gamine" variant="light" size="sm">
              Flamboyant Gamine vs Soft Gamine
            </Button>
            <Button component={Link} href="/kibbe/compare/gamine/soft-gamine" variant="light" size="sm">
              Gamine vs Soft Gamine
            </Button>
          </Group>
        </Box>

        <Divider />

        {/* The gamine face */}
        <Box>
          <Title order={2} fz={24} fw={400} mb={16}>
            The Gamine Face
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing={24}>
            <Text fz="md" c="dimmed" style={{ lineHeight: 1.8 }}>
              Every description of gamine beauty circles the same features: eyes that seem slightly too
              large for the face, a small pointed or tapered chin, expressive brows, and a mouth that sits
              somewhere between delicate and impish. The features are individually striking but none of them
              match — round eyes over sharp cheekbones, a soft mouth under a crisp jaw. On a bigger frame
              the same mix would read as interesting; compressed onto a small scale it reads as electric.
              That&apos;s also why gamine faces photograph &quot;young&quot; for decades — animation ages
              slower than symmetry.
            </Text>
            <Image
              src="/kibbe/hair/gamine.png"
              alt="Gamine haircuts: the crop, the textured pixie, and the chin bob with baby bangs"
              radius="md"
              loading="lazy"
            />
          </SimpleGrid>
        </Box>

        <Divider />

        {/* Style guide */}
        <Box>
          <Title order={2} fz={24} fw={400} mb={8}>
            Gamine Style: The Rules That Matter
          </Title>
          <Text fz="sm" c="dimmed" mb={24}>
            Gamine style has one governing principle: clothes should end somewhere definite. Definition
            flatters a compact mixed frame; flow erases it.
          </Text>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing={24}>
            <Paper p="xl" radius="md" withBorder>
              <Group gap={10} mb={16}>
                <ThemeIcon color="green" size={32} radius="xl" variant="light">
                  <IconCheck size={18} />
                </ThemeIcon>
                <Text fw={600}>Reach for</Text>
              </Group>
              <Stack gap={10}>
                {STYLE_RULES.map((rule, i) => (
                  <Group key={i} gap={10} wrap="nowrap" align="flex-start">
                    <Box w={6} h={6} mt={8} style={{ borderRadius: "50%", background: "var(--mantine-color-lime-6)", flexShrink: 0 }} />
                    <Text fz="sm">{rule}</Text>
                  </Group>
                ))}
              </Stack>
            </Paper>
            <Paper p="xl" radius="md" withBorder>
              <Group gap={10} mb={16}>
                <ThemeIcon color="red" size={32} radius="xl" variant="light">
                  <IconX size={18} />
                </ThemeIcon>
                <Text fw={600}>Skip</Text>
              </Group>
              <Stack gap={10} mb={16}>
                {STYLE_AVOID.map((rule, i) => (
                  <Group key={i} gap={10} wrap="nowrap" align="flex-start">
                    <Box w={6} h={6} mt={8} style={{ borderRadius: "50%", background: "var(--mantine-color-red-5)", flexShrink: 0 }} />
                    <Text fz="sm">{rule}</Text>
                  </Group>
                ))}
              </Stack>
              <Text fz="sm" c="dimmed" style={{ lineHeight: 1.6 }}>
                The classic mistake: a petite gamine buying the flowy maxi dress that looked incredible on
                her tall friend. Same dress, opposite frame, opposite result.
              </Text>
            </Paper>
          </SimpleGrid>
          <Group gap={12} mt={16}>
            <Button component={Link} href="/kibbe-outfits/gamine" variant="light" size="sm">
              Gamine outfit ideas
            </Button>
            <Button component={Link} href="/kibbe-outfits/soft-gamine" variant="light" size="sm">
              Soft Gamine outfits
            </Button>
            <Button component={Link} href="/kibbe-outfits/flamboyant-gamine" variant="light" size="sm">
              Flamboyant Gamine outfits
            </Button>
          </Group>
        </Box>

        <Divider />

        {/* Gamine vs adjacent words */}
        <Box>
          <Title order={2} fz={24} fw={400} mb={16}>
            Gamine vs. Pixie vs. Tomboy vs. Coquette
          </Title>
          <Text fz="md" c="dimmed" maw={760} style={{ lineHeight: 1.8 }}>
            These words get tangled together online, so here&apos;s the untangling. A <strong>pixie</strong>{" "}
            is a haircut — anyone can get one, though gamines wear it most natively. <strong>Tomboy</strong>{" "}
            is a clothing preference; it says nothing about your bones, and plenty of gamines dress in full
            skirts and bows. <strong>Coquette</strong> is an aesthetic trend built on ribbon-and-lace
            flirtatiousness — closer to Soft Gamine styling, but again, a wardrobe choice. <strong>Gamine</strong>{" "}
            is the only one of the four that describes a physical archetype: small scale, mixed features,
            animated presence. You can adopt the other three this afternoon; gamine you either are or
            aren&apos;t — and the fastest way to find out is a photo analysis, not a mirror argument.
          </Text>
        </Box>

        {/* CTA */}
        <Paper
          p={{ base: "xl", md: 48 }}
          radius="lg"
          ta="center"
          style={{ background: "linear-gradient(135deg, #66a80f 0%, #c0eb75 100%)" }}
        >
          <ThemeIcon size={56} radius="xl" variant="light" mb={16} style={{ background: "rgba(255,255,255,0.25)", color: "white" }}>
            <IconCamera size={30} />
          </ThemeIcon>
          <Title order={2} fz={{ base: 24, md: 32 }} fw={400} mb={12} c="white">
            Are You a Gamine? Find Out From a Photo
          </Title>
          <Text fz="md" mb={32} maw={560} mx="auto" style={{ color: "rgba(255,255,255,0.95)", lineHeight: 1.7 }}>
            Our AI reads your scale, bone structure, and feature mix — the three things that decide gamine —
            and tells you which of the 13 Kibbe types you actually are. Free, about two minutes.
          </Text>
          <Button
            component={Link}
            href="/kibbe-body-type-test"
            size="xl"
            rightSection={<IconArrowRight size={20} />}
            styles={{ root: { background: "white", color: "#66a80f", fontWeight: 600 } }}
          >
            Take the Free Kibbe Test
          </Button>
        </Paper>

        {/* FAQ */}
        <Box>
          <Title order={2} fz={24} fw={400} mb={16}>
            Gamine FAQ
          </Title>
          <Accordion variant="separated">
            {faqs.map((faq, i) => (
              <Accordion.Item key={i} value={`faq-${i}`}>
                <Accordion.Control>
                  <Text fw={500}>{faq.question}</Text>
                </Accordion.Control>
                <Accordion.Panel>
                  <Text fz="sm" c="dimmed" style={{ lineHeight: 1.7 }}>
                    {faq.answer}
                  </Text>
                </Accordion.Panel>
              </Accordion.Item>
            ))}
          </Accordion>
        </Box>

        {/* Related */}
        <Box>
          <Title order={3} fz={18} fw={500} mb={16}>
            Keep Going
          </Title>
          <Group gap={12}>
            <Button component={Link} href="/kibbe/gamine" variant="default" size="sm">
              Gamine Body Type Guide
            </Button>
            <Button component={Link} href="/kibbe-celebrities" variant="default" size="sm">
              Kibbe Celebrities
            </Button>
            <Button component={Link} href="/kibbe-body-types-guide" variant="default" size="sm">
              All 13 Kibbe Types
            </Button>
            <Button component={Link} href="/kibbe-body-type-test" variant="default" size="sm">
              Kibbe Body Type Test
            </Button>
          </Group>
        </Box>
      </Stack>
    </Container>
  );
}
